import Tabs from 'components/tabs-accordeon';
import { ISectionText } from 'global';
import ContentCmsContentStatic from 'modules/content-cms/components/content';
import { useContentful } from 'modules/content-cms/components/context';
import renderDocument from 'modules/content-cms/util/render-document';
import { StaticPDPProduct } from 'modules/product-details/types';
import React, { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useProductDetailsState } from '../../state';

interface PropTypes {
  className?: string;
  product: StaticPDPProduct;
}

interface BaseTab {
  title: string;
  content?: React.ReactNode;
}

interface ProductInformationTab extends BaseTab {
  title: string;
  content: React.JSX.Element;
}

export type ContentTabs = (BaseTab | ProductInformationTab)[];

/**
 * Content component that renders the content for the product details page below the product details header.
 */
const Content: React.FC<PropTypes> = ({ className, product }) => {
  const { formatMessage } = useIntl();
  const { data: contentful } = useContentful();
  const { contentTab, onContentTabChange } = useProductDetailsState();

  const contentTabs: ContentTabs = useMemo(() => {
    const sections = contentful.fields.sections || [];

    const productInformationTab: ProductInformationTab = {
      title: formatMessage({
        id: 'common:tabs-accordeon:product-info',
      }),
      content: <ContentCmsContentStatic />,
    };

    const tabs =
      (sections.filter(
        (section) => section.fields.productInformationTab
      ) as unknown as ISectionText[]) ?? [];

    const tabsContent: BaseTab[] = tabs.map((tab) => {
      return {
        title: tab.fields.richTitle ?? tab.fields.title ?? '',
        ...(tab.fields.text && { content: renderDocument(tab.fields.text) }),
      };
    });

    return [productInformationTab, ...tabsContent];
  }, [contentful, formatMessage]);

  return (
    <div data-test="tab-content-count" className={className}>
      <Tabs
        tabs={contentTabs}
        activeTab={contentTab}
        onContentTabChange={onContentTabChange}
        product={product}
        className="bg-other-white px-4 pb-3"
      />
    </div>
  );
};

export default memo(Content);
